import * as React from 'preact/compat'
import { batch, Signal, signal } from '@preact/signals'
import Api from '../../api'
import { captureFetchException, gameDateToInt } from '../../common/helpers'
import type { GameInfo, GameRelation, RelationType, SeriesInfoInterface } from '../SeriesEditor/types'
import { showToast } from '../toast'

export interface ISeriesSchemeState {
  // Deafult state
  loading: Signal<boolean>,
  seriesInfo: Signal<SeriesInfoInterface | null>,
  games: Signal<GameInfo[]>,
  relatedSeries: Signal<SeriesInfoInterface[]>,
  gamesRelations: Signal<GameRelation[]>,
  relationTypes: Signal<RelationType[]>,
  zoom: Signal<number>,
  gameElements: Signal<{
    [key: number]: HTMLElement & {
      precomputedBBox: DOMRect
    }
  }>,
  container: Signal<HTMLElement>,
  linesWrapper: Signal<SVGSVGElement>,
  highlightedGame: Signal<number>,
  highlightedRelations: Signal<GameRelation[]>,
  relatedGamesHighlights: Signal<Array<{
    id: number,
    text: string
  }>>,
  isMobile: Signal<boolean>,
  infoPanelGame: Signal<number>,
  isLoggedIn: Signal<boolean>,
  renderLines: Signal<boolean>,
  // Default methods
  load: (id: number) => void,
  setZoom: (value: number) => void,
  registerGameElement: (id: number, element: HTMLElement) => void,
  storeContainer: (element: HTMLElement) => void,
  setLinesWrapper: (element: SVGSVGElement) => void,
  highlightGame: (id: number) => void,
  clearHighlight: () => void,
  showInfoPanel: (id: number) => void
  // Editable state
  editable: Signal<boolean>,
  seriesEditor: Signal<boolean>,
  seriesRelationEditor: Signal<boolean>,
  gameSearchModal: Signal<boolean>,
  gameRelationsModal: Signal<GameInfo | false>,
  gameAddCallback: Signal<(game: GameInfo) => void>,
  // Editable methods
  showSeriesEditor: () => void,
  hideSeriesEditor: () => void,
  showSeriesRelationEditor: () => void,
  hideSeriesRelationEditor: () => void,
  addRelatedSeries: (series: SeriesInfoInterface) => void,
  removeSeriesRelation: (relatedSeries: SeriesInfoInterface) => void,
  showAddGameModal: () => void,
  hideAddGameModal: () => void,
  addGame: (game: GameInfo) => Promise<boolean>,
  addCallback: (gameId: number, game: {
    poster: string,
    status: number,
    url: string,
    name: string,
    releaseDate: string
  }) => void,
  showRelationsModal: (id: number) => void,
  hideRelationsModal: () => void,
  removeGame: (id: number) => void,
  setGameAddCallback: (callback: null | ((game: GameInfo) => void)) => void
}

export const createState = (options?: {
  editable?: boolean
}): ISeriesSchemeState => {
  // Default state
  const loading = signal(true)
  const seriesInfo = signal<SeriesInfoInterface | null>(null)
  const games = signal<GameInfo[]>([])
  const relatedSeries = signal<SeriesInfoInterface[]>([])
  const gamesRelations = signal<GameRelation[]>([])
  const relationTypes = signal<RelationType[]>([])
  const zoom = signal<number>(1)
  const gameElements = signal<{
    [key: number]: HTMLElement & {
      precomputedBBox: DOMRect
    }
  }>([])
  const container = signal<HTMLElement>(null)
  const linesWrapper = signal<SVGSVGElement>(null)
  const highlightedGame = signal<number>(null)
  const highlightedRelations = signal<GameRelation[]>([])
  const relatedGamesHighlights = signal<Array<{
    id: number,
    text: string
  }>>([])
  const isMobile = signal<boolean>(window.matchMedia('(max-width: 640px)').matches)
  const infoPanelGame = signal<number>(null)
  const isLoggedIn = signal<boolean>(false)
  const renderLines = signal<boolean>(false)

  // Default methods
  const load = (id: number) => {
    loading.value = true
    Api.get({ url: `game-series/info/${id}?full=true` })
      .then(response => {
        batch(() => {
          seriesInfo.value = response.seriesInfo
          games.value = response.games
          relatedSeries.value = response.relatedSeries
          gamesRelations.value = response.gamesRelations
          relationTypes.value = response.relationTypes
          isLoggedIn.value = response.isLoggedIn
          loading.value = false
        })
      })
      .catch(err => {
        showToast({
          type: 'error',
          text: 'Во время загрузки информации произошла ошибка'
        })
        captureFetchException(err)
        loading.value = false
      })
  }

  const setZoom = (value: number) => {
    zoom.value = value
  }

  const registerGameElement = (id: number, element: HTMLElement) => {
    gameElements.value[id] = (element as HTMLElement & {
      precomputedBBox: DOMRect
    })
  }

  const storeContainer = (element: HTMLElement) => {
    container.value = element
  }

  const setLinesWrapper = (element: SVGSVGElement) => {
    linesWrapper.value = element
  }

  const showInfoPanel = (id: number) => {
    if (editable.value) {
      return
    }
    infoPanelGame.value = id
  }

  const highlightGame = (id: number) => {
    batch(() => {
      highlightedGame.value = id
      highlightedRelations.value = []
      relatedGamesHighlights.value = []
      gamesRelations.value.forEach(relation => {
        if (relation.first_game_id === id || relation.second_game_id === id) {
          highlightedRelations.value.push(relation)
          const relationType = relationTypes.value.find(type => type.id === relation.relation_type)
          if (relation.first_game_id === id) {
            relatedGamesHighlights.value.push({
              id: relation.second_game_id,
              text: relationType.forward_order_name
            })
          } else {
            relatedGamesHighlights.value.push({
              id: relation.first_game_id,
              text: relationType.backward_order_name
            })
          }
        }
      })
      if (!isMobile.value) {
        showInfoPanel(id)
      }
    })
  }

  const clearHighlight = () => {
    batch(() => {
      highlightedGame.value = null
      highlightedRelations.value = []
      relatedGamesHighlights.value = []
      infoPanelGame.value = null
    })
  }

  window.addEventListener('resize', () => {
    const newIsMobile = window.matchMedia('(max-width: 640px)').matches
    if (isMobile.value !== newIsMobile) {
      isMobile.value = newIsMobile
    }
  })

  // Editable state
  const editable = signal<boolean>(typeof options.editable !== 'undefined' ? options.editable : false)
  const seriesEditor = signal<boolean>(false)
  const seriesRelationEditor = signal<boolean>(false)
  const gameSearchModal = signal<boolean>(false)
  const gameRelationsModal = signal<GameInfo | false>(false)
  const gameAddCallback = signal<(game: GameInfo) => void>(null)

  // Editable methods
  const showSeriesEditor = () => {
    seriesEditor.value = true
  }
  const hideSeriesEditor = () => {
    seriesEditor.value = false
  }

  const showSeriesRelationEditor = () => {
    seriesRelationEditor.value = true
  }

  const hideSeriesRelationEditor = () => {
    seriesRelationEditor.value = false
  }

  const addRelatedSeries = (series: SeriesInfoInterface) => {
    const newRelatedSeries = [...relatedSeries.value]
    newRelatedSeries.push(series)
    relatedSeries.value = newRelatedSeries
    Api.post({
      url: 'game-series/add-related-series',
      body: {
        seriesId: seriesInfo.value.id,
        relatedSeriesId: series.id
      }
    })
      .catch(error => {
        relatedSeries.value = relatedSeries.value.filter(existinSeries => existinSeries !== series)
        captureFetchException(error)
        showToast({
          type: 'error',
          text: 'При добавлении связанной серии произошла ошибка'
        })
      })
  }

  const removeSeriesRelation = (series: SeriesInfoInterface) => {
    let relatedSeriesArr = [...relatedSeries.value]
    const index = relatedSeriesArr.indexOf(series)
    if (index === -1) {
      showToast({
        type: 'error',
        text: 'Не удалось найти удаляемую серию в списке серий<br><b>Это ошибка в коде, обратись к программисту!</b>'
      })
      return
    }
    relatedSeriesArr.splice(index, 1);
    relatedSeries.value = relatedSeriesArr
    Api.post({
      url: 'game-series/delete-related-series',
      body: {
        seriesId: seriesInfo.value.id,
        relatedSeriesId: series.id
      }
    })
      .catch(error => {
        relatedSeriesArr.splice(index, 0, series)
        relatedSeries.value = relatedSeriesArr
        captureFetchException(error)
        showToast({
          type: 'error',
          text: 'При удалении связанной серии произошла ошибка'
        })
      })
  }

  const showAddGameModal = () => {
    gameSearchModal.value = true
  }

  const hideAddGameModal = () => {
    gameSearchModal.value = false
  }

  const addGame = (game: GameInfo) => {
    return new Promise<boolean>((resolve, reject) => {
      const newGames = [...games.value]
      newGames.push(game)
      newGames.sort((game1, game2) => gameDateToInt(game1.release_date) - gameDateToInt(game2.release_date))
      games.value = newGames
      Api.post({
        url: `game-series/add-game`,
        body: {
          seriesId: seriesInfo.value.id,
          gameId: game.id,
          type: game.type
        }
      })
        .then(() => {
          resolve(true)
        })
        .catch(error => {
          const index = newGames.findIndex(game2 => game2.id === game.id)
          newGames.splice(index, 1)
          games.value = newGames
          captureFetchException(error)
          showToast({
            type: 'error',
            text: 'При добавлении игры в серию произошла ошибка'
          })
          reject()
        })
    })
  }

  const addCallback = (gameId: number, game: {
    poster: string,
    status: number,
    url: string,
    name: string,
    releaseDate: string
  }) => {
    if (!gameRelationsModal.value) {
      if (games.value.find(game2 => game2.id === gameId)) {
        showToast({
          type: 'error',
          text: 'Игра уже есть в серии'
        })
        return
      }

      gameRelationsModal.value = {
        id: gameId,
        title: game.name,
        poster: game.poster,
        type: null,
        release_date: game.releaseDate
      }
    } else if (gameAddCallback.value) {
      gameAddCallback.value({
        id: gameId,
        title: game.name,
        poster: game.poster,
        type: null,
        release_date: game.releaseDate
      })
    }
  }

  const showRelationsModal = (id: number) => {
    const game = games.value.find(game => game.id === id)
    if (!game) {
      showToast({
        type: 'error',
        text: 'Не удалось найти игру в списке игр серии<br><b>Это ошибка в коде! Обратись к программисту!</b>'
      })
    }
    gameRelationsModal.value = game
  }

  const hideRelationsModal = () => {
    gameRelationsModal.value = false
  }

  const removeGame = (id: number) => {
    const index = games.value.findIndex(game => game.id === id)
    if (index === -1) {
      showToast({
        type: 'error',
        text: 'Не удалось найти игру в списке игр серии<br><b>Это ошибка в коде! Обратись к программисту!</b>'
      })
      return
    }
    const game = games.value[index]
    const newGames = [...games.value]
    newGames.splice(index, 1)
    games.value = newGames
    Api.post({
      url: 'game-series/remove-game',
      body: {
        seriesId: seriesInfo.value.id,
        gameId: id
      }
    })
      .then(() => {
        load(seriesInfo.value.id)
      })
      .catch(error => {
        newGames.splice(index, 0, game)
        games.value = newGames
        captureFetchException(error)
        showToast({
          type: 'error',
          text: 'При удалении игры произошла ошибка'
        })
      })
  }

  const setGameAddCallback = (callback: null | ((game: GameInfo) => void)) => {
    gameAddCallback.value = callback
  }

  return {
    // Default state
    loading,
    seriesInfo,
    games,
    relatedSeries,
    gamesRelations,
    relationTypes,
    zoom,
    gameElements,
    container,
    linesWrapper,
    highlightedGame,
    highlightedRelations,
    relatedGamesHighlights,
    isMobile,
    infoPanelGame,
    isLoggedIn,
    renderLines,
    // Default methods
    load,
    setZoom,
    registerGameElement,
    storeContainer,
    setLinesWrapper,
    highlightGame,
    clearHighlight,
    showInfoPanel,
    // Editable state
    editable,
    seriesEditor,
    seriesRelationEditor,
    gameSearchModal,
    gameRelationsModal,
    gameAddCallback,
    // Editable methods
    showSeriesEditor,
    hideSeriesEditor,
    showSeriesRelationEditor,
    hideSeriesRelationEditor,
    addRelatedSeries,
    removeSeriesRelation,
    showAddGameModal,
    hideAddGameModal,
    addGame,
    addCallback,
    showRelationsModal,
    hideRelationsModal,
    removeGame,
    setGameAddCallback,

  }
}

export const SeriesSchemeState = React.createContext<ISeriesSchemeState>(undefined)
SeriesSchemeState.displayName = 'SeriesSchemeContext'
